import {
  AgreementsResponse,
  MeterResponse,
  PaymentScheduleDetails,
} from "@/api/model"
import { DateFormats } from "@/lib/dateHelper"
import dayjs, { Dayjs } from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import minMax from "dayjs/plugin/minMax"
import { BadgeVariants } from "@/components/ui/badge"
import {
  StepItem,
  StepperItemState,
  StepperItemStateType,
} from "@/components/ui/stepper/StepperTypes"

dayjs.extend(minMax)
dayjs.extend(isSameOrAfter)

const LATE_SMART_METER_THRESHOLD_DAYS = 3

export const formatDateString = (date?: string) => formatDate(dayjs(date))

export const formatDate = (date: Dayjs) =>
  date.format(DateFormats.dayLongMonthShort)

export interface MeterPointDisplay {
  meterPointId: number
  fuelCapitalized: "Gas" | "Electricity"
  fuelLowercase: string
  iconStroke: string
  badgeText: string
  badgeVariant: BadgeVariants["variant"]
  meterStatusText: string
}

export const getSwitchCancellationDeadline = (
  meterPoints?: MeterResponse[],
) => {
  const cancellationDeadlineDates = meterPoints
    ?.map((meterPoint) => dayjs(meterPoint.switchCancellationDeadline))
    .filter((date) => {
      return date.isValid()
    })

  if (!cancellationDeadlineDates?.length) return undefined

  return dayjs.min(cancellationDeadlineDates)
}

enum ConciseMeterStatus {
  ON_SUPPLY_OMR_SUBMITTED,
  ON_SUPPLY_SMART_TAKING_OMR,
  ON_SUPPLY_SMART_LATE_OMR,
  ON_SUPPLY_ELECTRICITY_NO_OMR,
  NOT_ON_SUPPLY_SMART,
  OMR_WINDOW_STANDARD_REQUESTING_OMR,
  OMR_WINDOW_STANDARD_OMR_SUBMITTED,
  OMR_WINDOW_STANDARD_OMR_PENDING,
  OMR_WINDOW_STANDARD_OMR_REJECTED,
  BEFORE_OMR_WINDOW_STANDARD,
  METER_UNRESPONSIVE,
}

export const getConciseMeterStatus = (
  meterPoint: MeterResponse,
): ConciseMeterStatus => {
  if (
    !meterPoint.openingReadingWindowEndDate ||
    !meterPoint.openingReadingWindowStartDate
  ) {
    return ConciseMeterStatus.METER_UNRESPONSIVE
  }

  if (meterPoint.isSmartMeter) {
    if (isOnSupply(meterPoint)) {
      if (isOMRSubmitted(meterPoint)) {
        return ConciseMeterStatus.ON_SUPPLY_OMR_SUBMITTED
      } else if (isSmartMeterOMRLate(meterPoint)) {
        return ConciseMeterStatus.ON_SUPPLY_SMART_LATE_OMR
      }
      return ConciseMeterStatus.ON_SUPPLY_SMART_TAKING_OMR
    }
    return ConciseMeterStatus.NOT_ON_SUPPLY_SMART
  }

  if (meterPoint.isElectricity) {
    if (isOnSupply(meterPoint)) {
      if (isOMRSubmitted(meterPoint)) {
        return ConciseMeterStatus.ON_SUPPLY_OMR_SUBMITTED
      } else if (isDuringOMRWindow(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_REQUESTING_OMR
      }
      return ConciseMeterStatus.ON_SUPPLY_ELECTRICITY_NO_OMR
    }

    if (isDuringOMRWindow(meterPoint)) {
      if (isOMRAccepted(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_SUBMITTED
      }
      if (isOMRRejected(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_REJECTED
      }
      if (isOMRPending(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_PENDING
      }
      return ConciseMeterStatus.OMR_WINDOW_STANDARD_REQUESTING_OMR
    }
    return ConciseMeterStatus.BEFORE_OMR_WINDOW_STANDARD
  }

  if (meterPoint.isGas) {
    if (isOnSupply(meterPoint) || isDuringOMRWindow(meterPoint)) {
      if (isOMRAccepted(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_SUBMITTED
      }
      if (isOMRRejected(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_REJECTED
      }
      if (isOMRPending(meterPoint)) {
        return ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_PENDING
      }
      return ConciseMeterStatus.OMR_WINDOW_STANDARD_REQUESTING_OMR
    }
  }

  return ConciseMeterStatus.BEFORE_OMR_WINDOW_STANDARD
}

export const getConciseMeterStatusObject = (
  meterPoint: MeterResponse,
): MeterPointDisplay => {
  const fuelCapitalized = meterPoint.isGas ? "Gas" : "Electricity"
  const fuelLowercase = fuelCapitalized.toLowerCase()
  let badgeText = "Pending"
  let badgeVariant: BadgeVariants["variant"] = "primary"
  let meterStatusText = ""

  const status = getConciseMeterStatus(meterPoint)

  switch (status) {
    case ConciseMeterStatus.ON_SUPPLY_OMR_SUBMITTED:
      badgeText = "On supply"
      meterStatusText = `Your ${fuelLowercase} is now on supply`
      break
    case ConciseMeterStatus.ON_SUPPLY_SMART_TAKING_OMR:
      badgeText = "Taking opening read"
      break
    case ConciseMeterStatus.ON_SUPPLY_SMART_LATE_OMR:
      badgeText = "We need a reading"
      badgeVariant = "destructive"
      break
    case ConciseMeterStatus.ON_SUPPLY_ELECTRICITY_NO_OMR:
      badgeText = "On supply"
      meterStatusText = "Estimated reads will be taken from account"
      break
    case ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_SUBMITTED:
      badgeText = "Read submitted"
      meterStatusText = `Read submitted on ${formatDateString(getOMRProcessedDate(meterPoint))}`
      break
    case ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_PENDING:
      badgeText = "Read submitted"
      meterStatusText = "Opening meter reading is being processed"
      break
    case ConciseMeterStatus.OMR_WINDOW_STANDARD_REQUESTING_OMR:
      badgeText = "We need a read"
      badgeVariant = "destructive"
      meterStatusText = `Submit ${fuelLowercase} reading by ${formatDateString(meterPoint.openingReadingWindowEndDate)}`
      break
    case ConciseMeterStatus.BEFORE_OMR_WINDOW_STANDARD:
      meterStatusText = `${fuelCapitalized} reading submission opens on ${formatDateString(meterPoint.openingReadingWindowStartDate)}`
      break
    case ConciseMeterStatus.OMR_WINDOW_STANDARD_OMR_REJECTED:
      badgeText = "Read rejected"
      badgeVariant = "destructive"
      meterStatusText = "Must match your previous supplier's final reading"
      break
    default:
      //  NOT_ON_SUPPLY_SMART
      //  METER_UNRESPONSIVE
      break
  }

  return {
    meterPointId: meterPoint.meterpointID,
    fuelCapitalized,
    fuelLowercase,
    badgeText,
    badgeVariant,
    meterStatusText,
    iconStroke: meterPoint.isGas
      ? "stroke-secondary-400"
      : "stroke-primary-400",
  }
}

export const getMeterSteps = (meterPoints?: MeterResponse[]) => {
  const switchStartDate = getSwitchStartDate(meterPoints)

  const isEveryMeterPointOnSupply = !!meterPoints?.every((meterPoint) =>
    isOnSupply(meterPoint),
  )

  const someElectricityMeterPointsInOrAfterOMRWindow = meterPoints
    ?.filter((meterPoint) => meterPoint.isElectricity)
    .some(
      (elecMeterPoints) =>
        isDuringOMRWindow(elecMeterPoints) || isAfterOMRWindow(elecMeterPoints),
    )

  const steps: StepItem[] = [
    {
      state: StepperItemState.COMPLETE,
      title: "Switch in progress",
      description: switchStartDate
        ? switchStartDate.format(DateFormats.dayLongMonthShortNoYear)
        : "",
      isCompleted:
        isEveryMeterPointOnSupply ||
        someElectricityMeterPointsInOrAfterOMRWindow,
    },
  ]

  const electricityMeterPoints = meterPoints?.filter(
    (meterPoint) => meterPoint.isElectricity,
  )
  const allElectricityMeterPointsSmart = electricityMeterPoints?.every(
    (meterPoints) => meterPoints.isSmartMeter,
  )

  if (!allElectricityMeterPointsSmart) {
    steps.push(getElectricityStepperState(electricityMeterPoints))
  }

  steps.push({
    state: isEveryMeterPointOnSupply
      ? StepperItemState.COMPLETE
      : StepperItemState.IDLE,
    title: "On supply",
    description: getOnSupplyDescription(isEveryMeterPointOnSupply, meterPoints),
    isCompleted: isEveryMeterPointOnSupply,
  })

  if (electricityMeterPoints?.length && allElectricityMeterPointsSmart) {
    steps.push(getElectricityStepperState(electricityMeterPoints))
  }

  const gasMeterPoints = meterPoints?.filter((meterPoint) => meterPoint.isGas)
  if (gasMeterPoints?.length) {
    steps.push(getGasStepperState(gasMeterPoints))
  }

  return steps
}

const getElectricityStepperState = (
  electricityMeterPoints?: MeterResponse[],
) => {
  const isMultipleElectricity =
    electricityMeterPoints?.length && electricityMeterPoints?.length > 1

  let state: StepperItemStateType = StepperItemState.IDLE

  const anyElectricityOMRRejected = electricityMeterPoints?.some((meterPoint) =>
    isOMRRejected(meterPoint),
  )

  const anyElectricityOMRPending = electricityMeterPoints?.some((meterPoint) =>
    isOMRPending(meterPoint),
  )

  const allElectricityOMRProcessed = electricityMeterPoints?.every(
    (meterPoint) => isOMRAccepted(meterPoint),
  )
  if (anyElectricityOMRRejected) {
    state = StepperItemState.ERROR
  } else if (allElectricityOMRProcessed) {
    state = StepperItemState.COMPLETE
  }

  const allElectricityAfterSupplyStartDate = isTodayOrAfterSupplyStartDate(
    electricityMeterPoints,
  )

  const allElectricityOnSupply = electricityMeterPoints?.every((meterPoint) =>
    isOnSupply(meterPoint),
  )

  return {
    state: state,
    title: isMultipleElectricity
      ? "Electricity readings"
      : "Electricity reading",
    description: "",
    isCompleted:
      !anyElectricityOMRRejected &&
      !anyElectricityOMRPending &&
      (allElectricityOnSupply || allElectricityAfterSupplyStartDate),
  }
}

const getGasStepperState = (gasMeterPoints?: MeterResponse[]) => {
  const isMultipleGas = gasMeterPoints?.length && gasMeterPoints?.length > 1

  const allGasOMRProcessed = gasMeterPoints?.every((meterPoint) =>
    isOMRAccepted(meterPoint),
  )

  const allGasOnSupply = gasMeterPoints?.every((meterPoint) =>
    isOnSupply(meterPoint),
  )

  let state: StepperItemStateType = StepperItemState.IDLE

  const anyGasOMRRejected = gasMeterPoints?.some((meterPoint) =>
    isOMRRejected(meterPoint),
  )

  const anyGasOMRPending = gasMeterPoints?.some((meterPoint) =>
    isOMRPending(meterPoint),
  )

  if (anyGasOMRRejected) {
    state = StepperItemState.ERROR
  } else if (allGasOMRProcessed) {
    state = StepperItemState.COMPLETE
  }

  return {
    state: state,
    title: isMultipleGas ? "Gas readings" : "Gas reading",
    description: "",
    isCompleted: !anyGasOMRRejected && !anyGasOMRPending && allGasOnSupply,
  }
}

export const getDropdownContent = (
  paymentSchedule?: PaymentScheduleDetails,
  agreementsQuery?: AgreementsResponse[],
  metersQuery?: MeterResponse[],
) => {
  const contentList = [
    {
      description: "First Payment:",
      content: "Within 10 days of supply date",
    },
  ]

  const currentPaymentSchedule = paymentSchedule?.currentSchedule
  if (currentPaymentSchedule) {
    contentList.push({
      description: "Payment type:",
      content: currentPaymentSchedule.isSeasonal
        ? "Seasonal payments"
        : "Monthly payments",
    })
  }

  const supplyStartDate = getSupplyStartDate(metersQuery)
  contentList.push({
    description: "Your Switch completion:",
    content: supplyStartDate
      ? supplyStartDate.format(DateFormats.long)
      : "Pending",
  })

  const tariffName = agreementsQuery?.[0].displayName

  if (tariffName) {
    contentList.push({
      description: "Tariff:",
      content: tariffName,
    })
  }

  return contentList
}

const isOMRSubmitted = (meterPoint: MeterResponse) =>
  !!meterPoint.pendingSubmissionStatus?.submissionDate ||
  meterPoint.registers?.some(
    (meter) => !!meter.lastNonEstimatedReading || !!meter.lastSmartReading,
  )

const isOMRAccepted = (meterPoint: MeterResponse) =>
  !!meterPoint.registers?.some(
    (meter) => !!meter.lastNonEstimatedReading || !!meter.lastSmartReading,
  )

const isOMRRejected = (meterPoint: MeterResponse) =>
  meterPoint.pendingSubmissionStatus?.submissionStatus === "REJECTED"

const getOMRProcessedDate = (meterPoint: MeterResponse) => {
  for (const register of meterPoint.registers ?? []) {
    const reading =
      register.lastNonEstimatedReading ?? register.lastSmartReading
    if (reading?.readingDate) return reading.readingDate
  }
  return undefined
}

const isOMRPending = (meterPoint: MeterResponse) =>
  !!meterPoint.pendingSubmissionStatus?.submissionDate &&
  !meterPoint.registers?.some(
    (meter) => !!meter.lastNonEstimatedReading || !!meter.lastSmartReading,
  )

const isDuringOMRWindow = (meterPoint: MeterResponse) =>
  !isBeforeOMRWindow(meterPoint) && !isAfterOMRWindow(meterPoint)

const isBeforeOMRWindow = (meterPoint: MeterResponse) =>
  dayjs().isBefore(meterPoint.openingReadingWindowStartDate)

const isAfterOMRWindow = (meterPoint: MeterResponse) =>
  dayjs().isAfter(dayjs(meterPoint.openingReadingWindowEndDate))

const isOnSupply = (meterPoint: MeterResponse) =>
  meterPoint.simplifiedSupplyStatus === "ON_SUPPLY"

const getSupplyStartDate = (meterPoints?: MeterResponse[]) => {
  const supplyStartDates = meterPoints
    ?.map((meterPoint) => dayjs(meterPoint.supplyStartDate))
    .filter((date) => {
      return date.isValid()
    })

  if (!supplyStartDates?.length) return undefined

  return dayjs.min(supplyStartDates)
}

const isTodayOrAfterSupplyStartDate = (meterPoints?: MeterResponse[]) => {
  const supplyStartDate = getSupplyStartDate(meterPoints)
  if (!supplyStartDate) return false
  return dayjs().isSameOrAfter(supplyStartDate)
}

const getSwitchStartDate = (meterPoints?: MeterResponse[]) => {
  const switchStartDates = meterPoints
    ?.map((meterPoint) => dayjs(meterPoint.switchStartDate))
    .filter((date) => {
      return date.isValid()
    })

  if (!switchStartDates?.length) return undefined

  return dayjs.min(switchStartDates)
}

const isSmartMeterOMRLate = (meterPoint: MeterResponse) => {
  if (!meterPoint.supplyStartDate) return false
  return dayjs().isSameOrAfter(
    dayjs(meterPoint.supplyStartDate).add(
      LATE_SMART_METER_THRESHOLD_DAYS,
      "day",
    ),
  )
}

const getOnSupplyDescription = (
  isEveryMeterPointOnSupply: boolean,
  meterPoints?: MeterResponse[],
): string => {
  const hasOnlyGas = meterPoints?.every((meterPoint) => meterPoint.isGas)
  const hasOnlyElectricity = meterPoints?.every(
    (meterPoint) => meterPoint.isElectricity,
  )

  if (isEveryMeterPointOnSupply) {
    if (hasOnlyElectricity) {
      return "Electricity supply active"
    } else if (hasOnlyGas) {
      return "Gas supply active"
    }
    return "Both fuels active"
  }

  const supplyStartDate = getSupplyStartDate(meterPoints)
  return supplyStartDate
    ? `Exp. ${supplyStartDate.format(DateFormats.dayLongMonthShortNoYear)}`
    : ""
}
